import { ArrowLeft, Clock, Euro, Calendar, MapPin, Star, MessageCircle, Heart, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import AvailabilityCalendar from "@/components/AvailabilityCalendar";
import ChatSystem from "@/components/ChatSystem";
import EmbeddedMap from "@/components/EmbeddedMap";
import NavigationButtons from "@/components/NavigationButtons";
import BottomNavigation from "@/components/BottomNavigation";
import { toast } from "sonner";

const mockServices = [
  {
    id: "1",
    title: "Marketing Strategy Consulting",
    shortDescription: "Boost je bedrijf met professionele marketing strategieën en groeiplannen op maat. Perfect voor ambitious women on a mission die hun business naar het volgende level willen brengen.",
    fullDescription: "In deze uitgebreide 90-minuten strategiesessie duiken we diep in jouw business en marketing uitdagingen. We bespreken je huidige aanpak, identificeren groeimogelijkheden en creëren een praktisch actieplan dat je direct kunt implementeren. Perfect voor vrouwelijke ondernemers die hun zichtbaarheid willen vergroten en meer klanten willen aantrekken.",
    price: 125,
    duration: "90 min",
    availability: "Ma-Wo-Do tussen 10:00-16:00",
    rating: 4.9,
    reviewCount: 34,
    image: "https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&w=800",
    serviceType: "both" as const, // 'online' | 'in-person' | 'both'
    fullAddress: "Regentschapsstraat 58, 1000 Brussel, België",
    provider: {
      name: "Sarah van Dijk",
      avatar: "https://images.pexels.com/photos/3763188/pexels-photo-3763188.jpeg?auto=compress&cs=tinysrgb&w=200",
      location: "Brussel, België",
      verified: true,
      bio: "Marketing strategist met 8+ jaar ervaring in het helpen van vrouwelijke ondernemers. Gepassioneerd over het creëren van authentieke brands die resoneren met je ideale klanten."
    },
    features: [
      "Persoonlijke 1-op-1 strategiesessie",
      "Uitgebreid marketing actieplan",
      "Follow-up resources en templates",
      "Online of op locatie mogelijk",
      "30 dagen email support inbegrepen"
    ],
    tags: ["Marketing", "Strategie", "Business Growth", "Personal Branding"]
  }
  // Voeg eventueel meer toe voor test
];

const ServiceDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);

  const service = mockServices.find(s=>s.id === id);
  if (!service) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center bg-elegant text-center">
        <h1 className="text-2xl font-serif font-semibold mb-2 text-navy-800">Deze dienst bestaat niet</h1>
        <p className="text-navy-600 mb-6">Het opgegeven dienst-ID werd niet gevonden.</p>
        <Button onClick={() => navigate(-1)}>Ga terug</Button>
      </div>
    );
  }

  const handleViewAvailability = () => {
    console.log("Opening availability calendar");
    setCalendarOpen(true);
  };

  const handleBookingConfirmed = (date: Date, time: string) => {
    console.log("Booking confirmed:", { date, time });
    toast.success(`Afspraak bevestigd! Je ontvangt binnenkort een bevestigingsmail.`);
  };

  const handleSendMessage = () => {
    console.log("Opening chat with provider");
    setChatOpen(true);
  };
  const handleGoBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const toggleFavorite = () => {
    setIsFavorite(!isFavorite);
    toast.success(isFavorite ? "Verwijderd van favorieten" : "Toegevoegd aan favorieten");
  };
  return (
    <div className="min-h-screen bg-elegant">
      {/* Header with back button */}
      <div className="sticky top-0 z-10 glass-effect border-b border-cream-300">
        <div className="px-6 py-4">
          <div className="flex items-center gap-4 mb-4">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleGoBack}
              className="btn-elegant-secondary"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              <span>Terug</span>
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-serif text-navy-800">
                Service Details
              </h1>
              <p className="text-sm text-navy-600">
                Ontdek wat deze woman on a mission te bieden heeft
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleFavorite}
              className="hover:bg-cream-100"
            >
              <Heart className={`h-5 w-5 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-navy-600'}`} />
            </Button>
          </div>

          {/* Service Header */}
          <div className="card-premium p-4 mb-4">
            <div className="text-center">
              <div className="mb-2">
                <h1 className="script-text text-3xl text-navy-800">
                  Service Details
                </h1>
              </div>
              <p className="text-sm text-navy-600">
                Bekijk alle informatie en boek direct een afspraak
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-6 pb-24">
        {/* Service Photo/Banner */}
        <div className="relative mb-6 rounded-2xl overflow-hidden shadow-elegant-lg">
          <img 
            src={service.image} 
            alt={service.title}
            className="w-full h-64 md:h-80 object-cover"
            loading="lazy"
            width={800}
            height={320}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
          <div className="absolute top-4 left-4">
            <Badge className="bg-white/90 text-navy-700 hover:bg-white">
              Marketing Service
            </Badge>
          </div>
        </div>

        {/* Service Info Card */}
        <Card className="card-premium mb-6">
          <CardContent className="p-6">
            {/* Provider Info */}
            <div className="flex items-center gap-3 mb-4">
              <Avatar className="h-12 w-12">
                <AvatarImage src={service.provider.avatar} alt={service.provider.name} />
                <AvatarFallback className="bg-cream-200 text-navy-700">
                  {service.provider.name.split(' ').map(n => n[0]).join('')}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-serif font-semibold text-navy-800">{service.provider.name}</h3>
                  {service.provider.verified && (
                    <Award className="h-4 w-4 text-gold-500" />
                  )}
                </div>
                <p className="text-sm flex items-center gap-1 text-navy-600">
                  <MapPin className="h-3 w-3" />
                  {service.provider.location}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                <span className="text-sm font-medium text-navy-800">{service.rating}</span>
                <span className="text-xs text-navy-600">({service.reviewCount})</span>
              </div>
            </div>

            {/* Service Title */}
            <h2 className="text-3xl font-serif text-navy-800 mb-3">
              {service.title}
            </h2>

            {/* Short Description */}
            <p className="leading-relaxed mb-6 text-navy-700">
              {service.shortDescription}
            </p>

            {/* Service Details Grid */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              {/* Price */}
              <div className="bg-cream-50 rounded-xl p-4 border border-cream-200">
                <div className="flex items-center gap-2 mb-1">
                  <Euro className="h-4 w-4 text-navy-600" />
                  <span className="text-sm font-medium text-navy-700">Prijs</span>
                </div>
                <p className="text-2xl font-bold text-navy-800">€{service.price}</p>
                <p className="text-xs text-navy-600">per sessie</p>
              </div>

              {/* Duration */}
              <div className="bg-cream-50 rounded-xl p-4 border border-cream-200">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="h-4 w-4 text-navy-600" />
                  <span className="text-sm font-medium text-navy-700">Duur</span>
                </div>
                <p className="text-2xl font-bold text-navy-800">{service.duration}</p>
                <p className="text-xs text-navy-600">sessie lengte</p>
              </div>
            </div>

            {/* Availability - Now with interactive button */}
            <div className="bg-cream-50 rounded-xl p-4 mb-6 border border-cream-200">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-navy-600" />
                  <span className="text-sm font-medium text-navy-700">Beschikbaarheid</span>
                </div>
                <Button 
                  onClick={handleViewAvailability}
                  size="sm"
                  className="btn-elegant-primary text-xs px-3 py-1"
                >
                  Bekijk Agenda
                </Button>
              </div>
              <p className="font-medium text-navy-800">{service.availability}</p>
              <p className="text-xs mt-1 text-navy-600">Klik op "Bekijk Agenda" voor exacte tijden</p>
            </div>

            {/* Full Description */}
            <div className="mb-6">
              <h4 className="font-serif font-semibold text-navy-800 mb-3">Over deze service:</h4>
              <p className="leading-relaxed text-navy-700">{service.fullDescription}</p>
            </div>

            {/* Features List */}
            <div className="mb-6">
              <h4 className="font-serif font-semibold text-navy-800 mb-3">Wat krijg je:</h4>
              <div className="space-y-2">
                {service.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-gold-500" />
                    <span className="text-sm text-navy-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Tags */}
            <div className="mb-6">
              <h4 className="font-serif font-semibold text-navy-800 mb-3">Tags:</h4>
              <div className="flex flex-wrap gap-2">
                {service.tags.map((tag, index) => (
                  <Badge key={index} variant="secondary" className="bg-cream-100 text-navy-700">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Provider Bio */}
            <div className="bg-cream-50 rounded-xl p-4 border border-cream-200">
              <h4 className="font-serif font-semibold text-navy-800 mb-2">Over {service.provider.name}:</h4>
              <p className="text-sm text-navy-700 leading-relaxed">{service.provider.bio}</p>
            </div>
            {/* Woman on a Mission Badge */}
            <div className="bg-gold-50 rounded-lg p-4 mt-6 border border-gold-200">
              <p className="font-medium text-sm flex items-center gap-2 text-navy-800">
                <div className="gradient-gold text-white px-3 py-1 rounded-full text-xs font-bold">
                  WOM
                </div>
                Woman on a Mission - Geverifieerde Professional
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <Button 
            onClick={handleViewAvailability}
            className="btn-elegant-primary h-12 text-base"
          >
            <Calendar className="h-5 w-5 mr-2" />
            Boek Afspraak
          </Button>
          <Button 
            onClick={handleSendMessage}
            variant="outline"
            className="btn-elegant-secondary h-12 text-base"
          >
            <MessageCircle className="h-5 w-5 mr-2" />
            Stuur Bericht
          </Button>
        </div>

        {/* Embedded Map - Location & Navigation */}
        <EmbeddedMap
          location={service.provider.location}
          address={service.fullAddress}
          serviceType={service.serviceType}
          providerName={service.provider.name}
        />
        
        {/* Navigation Buttons */}
        <NavigationButtons
          location={service.provider.location}
          address={service.fullAddress}
          serviceType={service.serviceType}
        />
      </div>

      {/* Availability Calendar Popup */}
      <AvailabilityCalendar
        isOpen={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        onSelectSlot={handleBookingConfirmed}
      />

      {/* Chat System */}
      <ChatSystem
        isOpen={chatOpen}
        onClose={() => setChatOpen(false)}
        initialChatId={1} // This would be determined based on service provider
      />

      {/* Bottom Navigation */}
      <BottomNavigation />
    </div>
  );
};

export default ServiceDetail;